import { useCallback, useState } from 'react';
import { isEqual } from 'lodash';

interface FilterFilters {
  [key: string]: any;
}

interface FilterOrder {
  field: string;
  order: 'asc' | 'desc';
}

export type FilterOrders = FilterOrder[];

export interface FilterOptions {
  filter_filters?: FilterFilters;
  filter_order?: FilterOrders;
  limit: number;
  page: number;
}

const useFilters = (initialFilters: FilterFilters = {}, initialOrders: FilterOrders = [], startPage: number = 1, limit: number = 50, defaultFilterFilters: FilterFilters = {}) => {

  const [currentPage, setCurrentPage] = useState(startPage);
  const [currentPageSize, setCurrentPageSize] = useState(limit);
  const [initialFiltersState] = useState<FilterFilters>(initialFilters);
  const [initialOrdersState] = useState<FilterOrders>(initialOrders);

  const [filters, setFilters] = useState<FilterOptions>({
    filter_filters: initialFiltersState,
    filter_order: initialOrdersState,
    page: currentPage,
    limit: currentPageSize,
  });

  const updateFilters = (newFilters: FilterFilters) => {
    setFilters((prevFilters) => {
      const mergedFilters = {
        ...prevFilters.filter_filters,
        ...newFilters,
      };

      Object.keys(newFilters).forEach((key) => {
        if (newFilters[key] === null || newFilters[key] === undefined) {
          delete mergedFilters[key];
        }
      });

      if (isEqual(prevFilters.filter_filters, mergedFilters)) {
        return prevFilters;
      }

      return {
        ...prevFilters,
        filter_filters: mergedFilters,
      };
    });
  };

  const updateFilterOrder = useCallback(
    (keyValue: string, order: "asc" | "desc") => {
      const newFilterOrder: FilterOrders = [{
        field: keyValue,
        order: order
      }]

      setFilters((prevFilters) => {
        if (isEqual(prevFilters.filter_order, newFilterOrder)) {
          return prevFilters;
        }

        return {
          ...prevFilters,
          filter_order: newFilterOrder,
        };
      });
    }, [setFilters]);

  /* const getFilter = (key: string): any => {
    return filters.filter_filters && filters.filter_filters[key];
  } */

  const resetFilters = useCallback((limit: number = 50, hardReset: boolean = false) => {
    setFilters({
      filter_filters: hardReset ? {} : defaultFilterFilters,
      filter_order: hardReset ? [] : initialOrdersState,
      page: 1,
      limit: limit,
    });
  }, [defaultFilterFilters, initialOrdersState]);


  const updatePage = (pageSelected: any) => {
    pageSelected.selected++
    if (currentPage !== pageSelected.selected) {
      setCurrentPage(pageSelected.selected);
      setFilters((prevFilters) => ({
        ...prevFilters,
        page: pageSelected.selected,
      }));
    }
  }

  const setPage = (page: number) => {
    setCurrentPage(page);
    setFilters((prevFilters) => {
      if (prevFilters.page === page) {
        return prevFilters;
      }

      return {
        ...prevFilters,
        page,
      };
    });
  }

  const updatePageSize = (pageSelected: any) => {
    if (currentPageSize !== pageSelected.value) {
      setCurrentPageSize(pageSelected.value);
      setFilters((prevFilters) => ({
        ...prevFilters,
        limit: pageSelected.value,
      }));
    }
  }

  return { filters, updateFilters, resetFilters, updateFilterOrder, updatePage, updatePageSize, setPage };
};

export default useFilters;